import type {TreeNodeData} from '@mantine/core';
import type {TreeResponse} from 'src/api/ProactApiClient';

/**
 * Filter the tree nodes based on the search term and optional filter function, preserving hierarchy.
 * @registry Filters tree nodes by search term and optional predicate, preserving hierarchy.
 */
export const filterTreeData = (
  nodes: TreeNodeData[],
  searchTerm: string,
  filterFn?: (node: TreeNodeData) => boolean
): TreeNodeData[] => {
  const filteredNodes: TreeNodeData[] = [];

  const filterNode = (node: TreeNodeData): TreeNodeData | null => {
    let includeNode = false;
    const nodeLabel = String(node.label || '');
    const nodeLowerCase = nodeLabel.toLowerCase();

    // Check if the node itself contains the search term
    if (nodeLowerCase.includes(searchTerm.toLowerCase())) {
      includeNode = true;
    }

    // Apply additional filter if provided
    if (filterFn && !filterFn(node)) {
      return null; // Exclude this node and all its children
    }

    // If the node has children, filter them recursively
    let filteredChildren: TreeNodeData[] = [];
    if (node.children) {
      filteredChildren = node.children
        .map(child => filterNode(child))
        .filter(child => child !== null) as TreeNodeData[];
      if (filteredChildren.length > 0) {
        includeNode = true; // Include the parent if it has matching children
      }
    }

    // Only add the node if it matches the search term or has matching children
    if (includeNode) {
      return {
        label: node.label,
        value: node.value,
        children: filteredChildren.length > 0 ? filteredChildren : undefined,
      };
    }

    return null;
  };

  nodes.forEach(node => {
    const result = filterNode(node);
    if (result) {
      filteredNodes.push(result);
    }
  });

  return filteredNodes;
};

/**
 * @registry Finds a tree node by value across a forest of tree responses.
 */
export const findTreeNode = (
  id: string,
  nodes: TreeResponse[]
): TreeResponse | undefined => {
  for (const node of nodes) {
    const res = findTreeNodeRec(id, node);
    if (res) {
      return res;
    }
  }
  return;
};

const findTreeNodeRec = (
  id: string,
  node: TreeResponse
): TreeResponse | undefined => {
  if (node.value === id) {
    return node;
  }

  for (const child of node.children) {
    const res = findTreeNodeRec(id, child);
    if (res) {
      return res;
    }
  }

  return undefined;
};

/**
 * @registry A single ancestor entry (id + name) in a tree path.
 */
export interface TreeAncestor {
  id: string;
  name: string;
}

/**
 * Finds the ancestor path from root to the given node in the tree.
 * Returns an array of ancestors from root to the target node (inclusive).
 * @registry Root-to-node ancestor path for a target node ID within a tree forest.
 */
export const findAncestorsInTree = (
  targetId: string,
  nodes: TreeResponse[]
): TreeAncestor[] => {
  for (const node of nodes) {
    const path = findAncestorPath(targetId, node, []);
    if (path) {
      return path;
    }
  }
  return [];
};

const findAncestorPath = (
  targetId: string,
  node: TreeResponse,
  currentPath: TreeAncestor[]
): TreeAncestor[] | null => {
  const pathWithCurrent = [
    ...currentPath,
    {id: node.value, name: node.label},
  ];

  if (node.value === targetId) {
    return pathWithCurrent;
  }

  for (const child of node.children) {
    const result = findAncestorPath(targetId, child, pathWithCurrent);
    if (result) {
      return result;
    }
  }

  return null;
};

/**
 * @registry A flat node that can be folded into a TreeResponse forest.
 */
export interface FlatTreeNode {
  id: number;
  name: string;
  parentId?: number;
  kind?: string;
}

/**
 * Folds flat nodes into a TreeResponse forest rooted at `parentId`.
 * @registry Builds a TreeResponse forest from flat id/parentId nodes.
 */
export const buildTree = (
  nodes: FlatTreeNode[],
  parentId?: number
): TreeResponse[] => {
  const childrenByParent = new Map<number | undefined, FlatTreeNode[]>();
  for (const node of nodes) {
    const key = node.parentId ?? undefined;
    const siblings = childrenByParent.get(key);
    if (siblings) siblings.push(node);
    else childrenByParent.set(key, [node]);
  }
  const fold = (parent: number | undefined): TreeResponse[] =>
    (childrenByParent.get(parent) ?? []).map(node => ({
      value: String(node.id),
      label: node.name,
      kind: node.kind,
      children: fold(node.id),
    }));
  return fold(parentId ?? undefined);
};
